import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Activity, AlertCircle, Cpu, RefreshCw, ServerCog } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { StatPill } from '@/components/charts/StatPill';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Badge, stateVariant } from '@/components/ui/badge';
import { SortableTH, Table, TBody, TD, THead, TR } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, ErrorState } from '@/components/data/EmptyState';
import {
  JOB_STATE_COUNTS_ROW_CAP,
  shortenServerId,
  useActiveWorkerJobs,
  useHangfireServers,
  useJobStateCounts,
  useRecentJobs,
} from '@/hooks/useHangfire';
import { useTableSort } from '@/lib/useTableSort';
import { describeJob } from '@/lib/hangfire-format';
import { formatDurationMs, formatNumber, formatRelative } from '@/lib/utils';

const STATE_FILTERS = ['', 'Succeeded', 'Failed', 'Processing', 'Enqueued', 'Scheduled', 'Deleted'];

export function HangfireRoute() {
  // Deep-link target: /hangfire?job=<id> (e.g. from a triggered admin action's
  // Hangfire-job link) highlights that row in the recent-jobs table.
  const [searchParams] = useSearchParams();
  const highlightJob = searchParams.get('job');
  const [state, setState] = useState('');
  const [serverFilter, setServerFilter] = useState('');
  const [windowHours, setWindowHours] = useState(24);
  const servers = useHangfireServers();
  const activeWorkers = useActiveWorkerJobs();
  const counts = useJobStateCounts(windowHours);
  const jobs = useRecentJobs({ state: state || undefined, limit: 200 });

  // Worker/server options for the recent-jobs filter, derived from live servers.
  const serverOptions = useMemo(
    () => [...new Set((servers.data ?? []).map((s) => shortenServerId(s.id)))].sort(),
    [servers.data],
  );
  const visibleJobs = useMemo(
    () =>
      serverFilter
        ? (jobs.data ?? []).filter((j) => shortenServerId(j.serverId) === serverFilter)
        : (jobs.data ?? []),
    [jobs.data, serverFilter],
  );

  // Worker servers (2 cols): heartbeat sorts by timestamp.
  const serversSort = useTableSort(servers.data ?? []);

  // Active workers (4 cols): worker id shortened for sort to match display;
  // "Running for" sorts by raw ageSeconds, not the formatted duration string.
  const activeSort = useTableSort(activeWorkers.data ?? [], {
    accessors: {
      serverId: (w) => shortenServerId(w.serverId),
      ageSeconds: (w) => w.ageSeconds,
    },
  });

  // Recent jobs (7 cols, sorts the loaded 200 slice): duration sorts by the raw
  // ms value, worker by the shortened id, invocation by its summarized label.
  const jobsSort = useTableSort(visibleJobs, {
    accessors: {
      serverId: (j) => shortenServerId(j.serverId),
      durationMs: (j) => (j.durationMs == null ? null : j.durationMs),
      invocationdata: (j) => summarizeInvocation(j.invocationdata),
    },
  });

  const stateCounts = counts.data?.counts ?? [];
  const countsTruncated = counts.data?.truncated ?? false;
  const total = stateCounts.reduce((a, c) => a + c.count, 0);
  const failed = stateCounts.find((c) => c.state === 'Failed')?.count ?? 0;
  const succeeded = stateCounts.find((c) => c.state === 'Succeeded')?.count ?? 0;
  // Suppress the ratio when the scan was truncated — a percentage derived
  // from a partial slice is misleading regardless of how we label it.
  // The hint below reads "success rate unavailable (partial scan)" when
  // this is null, otherwise the ratio.
  const successRate = countsTruncated || total === 0
    ? null
    : Math.round((succeeded / total) * 100);

  return (
    <>
      <PageHeader
        title="Hangfire observability"
        description="Recurring jobs, worker servers, and per-state job throughput. Backed by the Hangfire.PostgreSql tables in schema `harvest`."
        actions={
          <div className="flex items-center gap-2">
            <Select value={windowHours} onChange={(e) => setWindowHours(Number(e.target.value))}>
              <option value={1}>Last 1h</option>
              <option value={6}>Last 6h</option>
              <option value={24}>Last 24h</option>
              <option value={168}>Last 7d</option>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void servers.refetch();
                void activeWorkers.refetch();
                void counts.refetch();
                void jobs.refetch();
              }}
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
          </div>
        }
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {/*
          When the state-counts query fails, total/succeeded/failed all
          collapse to 0 (counts.data is undefined). Showing those as
          authoritative "0" values would hide the error — the State
          distribution card below surfaces the actual error message.
          Render "—" instead until the query recovers.
        */}
        <StatPill
          label="Jobs in window"
          value={
            counts.isLoading || counts.isError
              ? '—'
              : countsTruncated
                ? `${formatNumber(total)}+`
                : formatNumber(total)
          }
          icon={Activity}
          hint={countsTruncated ? 'partial scan' : undefined}
        />
        <StatPill
          label="Succeeded"
          value={counts.isLoading || counts.isError ? '—' : formatNumber(succeeded)}
          hint={
            counts.isError
              ? undefined
              : successRate === null
                ? countsTruncated
                  ? 'success rate unavailable (partial scan)'
                  : undefined
                : `${successRate}% success rate`
          }
        />
        <StatPill
          label="Failed"
          value={counts.isLoading || counts.isError ? '—' : formatNumber(failed)}
          icon={AlertCircle}
        />
        <StatPill
          label="Live servers"
          value={servers.isLoading || servers.isError ? '—' : (servers.data?.length ?? 0)}
          icon={ServerCog}
        />
      </div>

      {countsTruncated && (
        <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-foreground">
          State counts hit the {formatNumber(JOB_STATE_COUNTS_ROW_CAP)}-row scan cap — every
          number above (jobs in window, succeeded, failed, success rate) is
          computed from a partial slice and is an under-count of the full
          window. Shorten the window or use SQL aggregation directly until a
          server-side rollup view lands.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>State distribution</CardTitle>
            <CardDescription>Job rows grouped by state in window.</CardDescription>
          </CardHeader>
          <CardContent>
            {counts.isError ? (
              <ErrorState error={counts.error} />
            ) : counts.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : stateCounts.length ? (
              <ul className="space-y-2">
                {stateCounts.map((s) => (
                  <li key={s.state} className="flex items-center justify-between text-sm">
                    <Badge variant={stateVariant(s.state)}>{s.state}</Badge>
                    <span className="tabular-nums">{formatNumber(s.count)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-xs text-muted-foreground">No jobs in window.</div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Worker servers</CardTitle>
            <CardDescription>Servers reporting heartbeats. Stale heartbeats indicate a crashed worker.</CardDescription>
          </CardHeader>
          <CardContent>
            {servers.isError ? (
              <ErrorState error={servers.error} />
            ) : servers.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : servers.data?.length ? (
              <Table>
                <THead>
                  <TR>
                    <SortableTH label="Server" sortKey="id" active={serversSort.sortKey === 'id'} direction={serversSort.sortDir} onSort={serversSort.toggle} />
                    <SortableTH label="Heartbeat" sortKey="lastheartbeat" active={serversSort.sortKey === 'lastheartbeat'} direction={serversSort.sortDir} onSort={serversSort.toggle} />
                  </TR>
                </THead>
                <TBody>
                  {serversSort.sorted.map((s) => (
                    <TR key={s.id}>
                      <TD className="font-mono text-xs">{s.id}</TD>
                      <TD>{formatRelative(s.lastheartbeat)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            ) : (
              <EmptyState
                icon={ServerCog}
                title="No Hangfire servers reporting"
                description="Make sure RevoDataPlatform.Host is running and connected to this Supabase instance."
              />
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Active workers</CardTitle>
          <CardDescription>
            Jobs currently held by a worker, and how long each has been running. A
            long-running row is a worker that's blocked on that job.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {activeWorkers.isError ? (
            <ErrorState error={activeWorkers.error} />
          ) : activeWorkers.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : activeWorkers.data?.length ? (
            <Table>
              <THead>
                <TR>
                  <SortableTH label="Worker" sortKey="serverId" active={activeSort.sortKey === 'serverId'} direction={activeSort.sortDir} onSort={activeSort.toggle} />
                  <SortableTH label="Processing" sortKey="jobType" active={activeSort.sortKey === 'jobType'} direction={activeSort.sortDir} onSort={activeSort.toggle} />
                  <SortableTH label="Running for" sortKey="ageSeconds" active={activeSort.sortKey === 'ageSeconds'} direction={activeSort.sortDir} onSort={activeSort.toggle} />
                  <SortableTH label="Job" sortKey="id" active={activeSort.sortKey === 'id'} direction={activeSort.sortDir} onSort={activeSort.toggle} />
                </TR>
              </THead>
              <TBody>
                {activeSort.sorted.map((w) => (
                  <TR key={w.id}>
                    <TD className="font-mono text-xs">{shortenServerId(w.serverId)}</TD>
                    <TD className="font-medium">{w.jobType}</TD>
                    <TD className="tabular-nums text-muted-foreground">
                      {formatDurationMs(w.ageSeconds * 1000)}
                    </TD>
                    <TD className="font-mono text-[11px] text-muted-foreground">#{w.id}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          ) : (
            <EmptyState icon={Cpu} title="No jobs are processing" description="All workers are idle." />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-end justify-between gap-3 space-y-0">
          <div>
            <CardTitle>Recent jobs</CardTitle>
            <CardDescription>The 200 most recent rows from the Hangfire `job` table.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={serverFilter} onChange={(e) => setServerFilter(e.target.value)} className="w-44">
              <option value="">All workers</option>
              {serverOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
            <Select value={state} onChange={(e) => setState(e.target.value)} className="w-40">
              {STATE_FILTERS.map((s) => (
                <option key={s} value={s}>
                  {s || 'All states'}
                </option>
              ))}
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {jobs.isError ? (
            <ErrorState error={jobs.error} />
          ) : jobs.isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : visibleJobs.length ? (
            <Table>
              <THead>
                <TR>
                  <SortableTH label="ID" sortKey="id" active={jobsSort.sortKey === 'id'} direction={jobsSort.sortDir} onSort={jobsSort.toggle} />
                  <SortableTH label="State" sortKey="statename" active={jobsSort.sortKey === 'statename'} direction={jobsSort.sortDir} onSort={jobsSort.toggle} />
                  <SortableTH label="Worker" sortKey="serverId" active={jobsSort.sortKey === 'serverId'} direction={jobsSort.sortDir} onSort={jobsSort.toggle} />
                  <SortableTH label="Duration" sortKey="durationMs" active={jobsSort.sortKey === 'durationMs'} direction={jobsSort.sortDir} onSort={jobsSort.toggle} />
                  <SortableTH label="Created" sortKey="createdat" active={jobsSort.sortKey === 'createdat'} direction={jobsSort.sortDir} onSort={jobsSort.toggle} />
                  <SortableTH label="Expires" sortKey="expireat" active={jobsSort.sortKey === 'expireat'} direction={jobsSort.sortDir} onSort={jobsSort.toggle} />
                  <SortableTH label="Invocation" sortKey="invocationdata" active={jobsSort.sortKey === 'invocationdata'} direction={jobsSort.sortDir} onSort={jobsSort.toggle} />
                </TR>
              </THead>
              <TBody>
                {jobsSort.sorted.map((j) => (
                  <TR
                    key={j.id}
                    className={highlightJob && String(j.id) === highlightJob ? 'bg-primary/10 ring-1 ring-primary/40' : undefined}
                  >
                    <TD className="font-mono text-xs">{j.id}</TD>
                    <TD>
                      <Badge variant={stateVariant(j.statename)}>{j.statename}</Badge>
                    </TD>
                    <TD className="font-mono text-xs text-muted-foreground">{shortenServerId(j.serverId)}</TD>
                    <TD className="tabular-nums text-muted-foreground">{formatDurationMs(j.durationMs)}</TD>
                    <TD>{formatRelative(j.createdat)}</TD>
                    <TD>{j.expireat ? formatRelative(j.expireat) : '—'}</TD>
                    <TD className="max-w-md truncate font-mono text-[11px] text-muted-foreground">
                      {summarizeInvocation(j.invocationdata)}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          ) : (
            <EmptyState
              icon={Activity}
              title="No jobs match the current filter"
              description={serverFilter ? `No recent jobs ran on ${serverFilter}.` : undefined}
            />
          )}
        </CardContent>
      </Card>
    </>
  );
}

/**
 * Hangfire's invocationdata column is `jsonb`, so PostgREST returns it
 * already-parsed (an object), not a JSON string. Older Hangfire versions
 * stored it as `text`, so we still tolerate string-shaped values.
 */
function summarizeInvocation(raw: unknown): string {
  if (raw == null) return '—';
  // Enum-dispatched Presto jobs get their target appended (e.g.
  // "PrestoHarvestJob — Event Team Stats") instead of the bare class.method;
  // see lib/hangfire-format.
  const label = describeJob(raw);
  if (label) return label;
  // No resolvable Type — fall back to a truncated raw view.
  const display = typeof raw === 'string' ? raw : JSON.stringify(raw);
  return display.slice(0, 80);
}
